import React, { useEffect, useState } from 'react';
import { CalendarX } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { useSession } from '../../lib/SessionContext';
import { t } from '../../i18n';
import { Card, Field, Alert, Spinner } from '../../components/ui';
import AvailabilityEditor, { withKey } from '../../components/AvailabilityEditor';
import { findProblem, dayKeyOf } from '../../lib/availability';
import { useUnsaved } from '../../lib/unsavedChanges';
import { inRange, weekPayload, weekSignature } from '../../lib/settingsForm';
import SettingsCard, { useSettingsCard, NumberWithUnit, GoButton } from './SettingsCard';

/**
 * When and where the professional sees patients: how long a session lasts, and
 * the week in blocks by sede (0044). Both decide what can be booked, so they
 * share a tab, and the week counts sessions with the length as typed, before it
 * is saved. A one-off block, like a holiday, has a date, so it stays under the
 * week in Agenda semanal; the tab says where.
 */
const HoursTab = () => {
  const { profile, refresh } = useSession();
  const length = useSettingsCard('session-length', {
    default_session_minutes: profile.default_session_minutes ?? 45,
  });

  // The week as edited, and as last saved, compared by what would be saved.
  const [blocks, setBlocks] = useState(null);
  const [savedWeek, setSavedWeek] = useState(null);
  const [sedes, setSedes] = useState([]);
  const [hasOnline, setHasOnline] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const weekChanged = blocks !== null && weekSignature(blocks) !== savedWeek;
  useUnsaved('hours', weekChanged);

  useEffect(() => {
    const load = async () => {
      const [blockResult, locationResult] = await Promise.all([
        supabase.from('therapist_availability')
          .select('id, weekday, starts_at, ends_at, location_id, allows_online, requires_approval')
          .eq('therapist_id', profile.id)
          .order('weekday').order('starts_at'),
        supabase.from('locations').select('id, name, is_online').eq('is_active', true).order('name'),
      ]);
      if (blockResult.error) { setError(blockResult.error.message); return; }
      const week = (blockResult.data ?? []).map(withKey);
      setBlocks(week);
      setSavedWeek(weekSignature(week));
      setSedes((locationResult.data ?? []).filter((location) => !location.is_online));
      setHasOnline((locationResult.data ?? []).some((location) => location.is_online));
    };
    load();
  }, [profile.id]);

  // Booking reads the length when a session is made, so sessions already
  // booked keep theirs.
  const saveLength = () => length.save(async (typed) => {
    if (!inRange(typed.default_session_minutes, [15, 180])) return t('mySettings.sessionLengthInvalid');
    const { error: saveError } = await supabase.from('therapists')
      .update({ default_session_minutes: Number(typed.default_session_minutes) })
      .eq('id', profile.id);
    if (saveError) return saveError.message;
    refresh();
    return null;
  });

  const saveWeek = async () => {
    setError(null);
    setNotice(null);
    // The database refuses a bad week too, but saying so here names the day.
    const problem = findProblem(blocks);
    if (problem) {
      setError(t(`availability.problems.${problem.problem}`,
        { day: t(`weekdays.${dayKeyOf(problem.weekday)}`).toLowerCase() }));
      return;
    }
    setSaving(true);
    // The whole week in one call, which replaces it or changes nothing.
    const { data, error: saveError } = await supabase.rpc('set_my_availability', { p_blocks: weekPayload(blocks) });
    setSaving(false);
    if (saveError) { setError(saveError.message); return; }
    const week = (data ?? []).map(withKey);
    setBlocks(week);
    setSavedWeek(weekSignature(week));
    setNotice(t('mySettings.hoursSaved'));
  };

  return (
    <div className="space-y-4">
      <SettingsCard
        title={t('mySettings.sessionLengthTitle')}
        subtitle={t('mySettings.sessionLengthHint')}
        card={length.card}
        onSave={saveLength}
      >
        <Field label={t('mySettings.sessionLengthLabel')}>
          <NumberWithUnit
            min="15" max="180" unit={t('settings.minutes')}
            value={length.form.default_session_minutes}
            onChange={(event) => length.change({ default_session_minutes: event.target.value })}
          />
        </Field>
      </SettingsCard>

      {blocks === null ? (
        <Card title={t('profile.hours')}>
          {error ? <Alert tone="danger">{error}</Alert> : <Spinner />}
        </Card>
      ) : (
        <SettingsCard
          title={t('profile.hours')}
          subtitle={t('availability.hint')}
          card={{ dirty: weekChanged, saving, error, notice }}
          onSave={saveWeek}
          after={(
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="flex items-start gap-2 text-sm text-slate-600">
                <CalendarX size={16} className="mt-0.5 shrink-0 text-slate-400" />
                {t('mySettings.blocksPointer')}
              </p>
              <GoButton to="/reservar">{t('mySettings.blocksLink')}</GoButton>
            </div>
          )}
        >
          <AvailabilityEditor
            blocks={blocks}
            sedes={sedes}
            hasOnline={hasOnline}
            sessionMinutes={Number(length.form.default_session_minutes) || 45}
            onChange={(next) => { setBlocks(next); setNotice(null); }}
            onNotice={setNotice}
          />
        </SettingsCard>
      )}
    </div>
  );
};

export default HoursTab;
